/**
 * Handoff — bridges the Coach's typed `DraftShape` into the Spark
 * wizard's form-value shape and advances the Spark store to Step 6
 * (Preview). A flag on the Spark store marks that the draft was
 * Coach-filled so the Preview step can show the banner.
 */

import type { ParameterDef, PortDefinition } from '@/api/types';

import { useSparkStore } from '@/features/spark/store';
import type {
  ParameterValues,
  PortValues,
  Step1Values,
  Step2Values,
  Step3Values,
  Step4Values,
} from '@/features/spark/schemas';

import type { DraftShape } from './engine';

export function applyDraftToSpark(draft: DraftShape): void {
  const step1: Step1Values = {
    name: draft.name,
    description: draft.description,
    version: draft.version,
    domain: draft.domain as Step1Values['domain'],
    author_name: draft.author_name,
  };
  const step2: Step2Values = { inputs: draft.inputs.map(portToValues) };
  const step3: Step3Values = { outputs: draft.outputs.map(portToValues) };
  const step4: Step4Values = { parameters: draft.parameters.map(paramToValues) };

  const spark = useSparkStore.getState();
  spark.reset();
  spark.applyStep1(step1);
  spark.applyStep2(step2);
  spark.applyStep3(step3);
  spark.applyStep4(step4);
  spark.markCoachFilled();
  spark.setStep(6);
}

function portToValues(p: PortDefinition): PortValues {
  return {
    name: p.name,
    data_type: isStringDataType(p.data_type) ? p.data_type : 'Document',
    required: p.required,
    schema_text: p.schema ? JSON.stringify(p.schema, null, 2) : '',
  };
}

function isStringDataType(
  t: PortDefinition['data_type'],
): t is PortValues['data_type'] {
  return typeof t === 'string';
}

function paramToValues(p: ParameterDef): ParameterValues {
  const { kind, default_text } = splitParameterValue(p.default);
  const range = splitParameterRange(p.range);
  return {
    name: p.name,
    description: p.description,
    kind,
    default_text,
    range_min: range.min,
    range_max: range.max,
    enum_variants: range.enum_variants,
  };
}

function splitParameterValue(v: ParameterDef['default']): {
  kind: ParameterValues['kind'];
  default_text: string;
} {
  if ('integer' in v) return { kind: 'Integer', default_text: String(v.integer) };
  if ('float' in v) return { kind: 'Float', default_text: String(v.float) };
  if ('bool' in v) return { kind: 'Bool', default_text: v.bool ? 'true' : 'false' };
  if ('string' in v) return { kind: 'String', default_text: v.string };
  return { kind: 'Enum', default_text: v.enum };
}

function splitParameterRange(
  r: ParameterDef['range'],
): { min: string; max: string; enum_variants: string } {
  if (!r) return { min: '', max: '', enum_variants: '' };
  if ('Integer' in r) return { min: String(r.Integer.min), max: String(r.Integer.max), enum_variants: '' };
  if ('Float' in r) return { min: String(r.Float.min), max: String(r.Float.max), enum_variants: '' };
  if ('Enum' in r) return { min: '', max: '', enum_variants: r.Enum.variants.join(', ') };
  return { min: '', max: '', enum_variants: '' };
}
